"use client"

import { useState, useEffect } from "react"
import {
  Box,
  Tabs,
  Tab,
  Container,
  Typography,
  Grid,
  Backdrop,
  CircularProgress,
  Button,
  AppBar,
  Toolbar,
  IconButton,
} from "@mui/material"
import { Menu as MenuIcon, NotificationsActive, Construction } from "@mui/icons-material"
import Select from "react-select"
import { MdRefresh } from "react-icons/md"

// Import Deep Dive components
import DeepDiveOverview from "../components/DeepDive/DeepDiveOverview"
import SectionsComparison from "../components/DeepDive/SectionsComparison"
import KpiBenchmark from "../components/DeepDive/KpiBenchmark"
import MaterialityComparison from "../components/DeepDive/MaterialityComparison"
import BaselineTemplate from "../components/DeepDive/BaselineTemplate"
import QuotesComparison from "../components/DeepDive/QuotesComparison"
import Chat from "../components/DeepDive/Chat"
import ReportUpload from "../components/DeepDive/ReportUpload"
import DeepDiveNarrative from "../components/DeepDive/DeepDiveNarrative"
import MaterialityExplorer from "../components/DeepDive/MaterialityExplorer"
import ExpandedKpiLibrary from "../components/DeepDive/ExpandedKpiLibrary"
import ExportCollaboration from "../components/DeepDive/ExportCollaboration"

// Tab definitions
const TABS = [
  { label: "Overview", component: DeepDiveOverview },
  { label: "Sections", component: SectionsComparison },
  { label: "KPIs", component: KpiBenchmark },
  { label: "Materiality", component: MaterialityComparison },
  { label: "Quotes", component: QuotesComparison },
  { label: "Baseline", component: BaselineTemplate },
  { label: "Chat", component: Chat },
  { label: "Upload", component: ReportUpload, comingSoon: true },
  { label: "Narrative", component: DeepDiveNarrative, comingSoon: true },
  { label: "Explorer", component: MaterialityExplorer, comingSoon: true },
  { label: "Expanded KPIs", component: ExpandedKpiLibrary, comingSoon: true },
  { label: "Export", component: ExportCollaboration, comingSoon: true },
]

// Coming Soon Component
const ComingSoon = () => (
  <Box
    sx={{
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      justifyContent: "center",
      minHeight: "400px",
      textAlign: "center",
      p: 4,
    }}
  >
    <Construction
      sx={{
        fontSize: 64,
        color: "#407775",
        mb: 3,
        opacity: 0.7,
      }}
    />
    <Typography
      variant="h3"
      sx={{
        color: "#407775",
        fontWeight: "bold",
        mb: 2,
      }}
    >
      Coming Soon
    </Typography>
    <Typography
      variant="h6"
      sx={{
        color: "#666",
        mb: 3,
      }}
    >
      This feature is under development
    </Typography>
    <Box
      sx={{
        width: 100,
        height: 4,
        backgroundColor: "#407775",
        borderRadius: 2,
      }}
    />
  </Box>
)

function TabPanel(props) {
  const { children, value, index, ...other } = props
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`deep-dive-tabpanel-${index}`}
      aria-labelledby={`deep-dive-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  )
}

const customStyles = {
  control: (provided, state) => ({
    ...provided,
    borderColor: "#00366D26",
    background: "#F5F5F5",
    boxShadow: "none",
    "&:hover": {
      borderColor: "#407775",
    },
    color: "#707070!important",
    position: "relative",
    minHeight: "38px",
    height: "38px",
  }),
  valueContainer: (provided) => ({
    ...provided,
    height: "38px",
    padding: "0 8px",
  }),
  input: (provided) => ({
    ...provided,
    margin: "0px",
  }),
  indicatorsContainer: (provided) => ({
    ...provided,
    height: "38px",
  }),
  dropdownIndicator: (provided) => ({
    ...provided,
    color: "#707070",
    width: "32px",
    display: "flex",
    alignItems: "center",
    "&:hover": {
      color: "#707070",
    },
  }),
  indicatorSeparator: () => ({
    display: "none",
  }),
  menu: (provided) => ({
    ...provided,
    zIndex: 9999,
  }),
  option: (provided, state) => ({
    ...provided,
    backgroundColor: state.isFocused || state.isSelected ? "#eef1f5" : "white",
    color: state.isSelected ? "#407775" : "#707070",
    "&:active": {
      backgroundColor: "#eef1f5",
    },
  }),
  placeholder: (provided) => ({
    ...provided,
    color: "#707070",
  }),
}

const DeepDivePage = () => {
  const [activeTab, setActiveTab] = useState(0)
  const [loading, setLoading] = useState(false)
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false)
  const [formData, setFormData] = useState({
    companyName: null,
    sector: [],
    country: [],
    competitors: [],
    year: null,
    query: "",
  })
  const [formData1, setFormData1] = useState({ companyName: null, competitors: [], year: null, query: "" })
  const [companyOptions, setCompanyOptions] = useState([])
  const [sectorOptions, setSectorOptions] = useState([])
  const [countryOptions, setCountryOptions] = useState([])
  const [competitorsOptions, setCompetitorsOptions] = useState([])
  const [yearOptions, setYearOptions] = useState([])
  const [kpiData, setKpiData] = useState({
    company_count: 0,
    average_csrd_page_count: 0,
    average_material_topics_count: 0,
  })
  const [chartData, setChartData] = useState({
    external_assurance_percent: 0,
    eu_taxonomy_percent: 0,
    companies_by_country: [],
    emissions_profile: [],
    workforce_and_board_diversity: [],
    iro_visual_format: {},
    esrs_standards_mentioned: [],
    framework_alignment: [],
  })
  const [benchmarkData, setBenchmarkData] = useState({})
  const [sectionsData, setSectionsData] = useState({})
  const [materialityComparisonData, setMaterialityComparisonData] = useState({})
  const [quotesComparisonData, setQuotesComparisonData] = useState({})
  const [chatData, setChatData] = useState({})
  const [localQuery, setLocalQuery] = useState("")

  const resetSelections = () => {
    setFormData({ companyName: null, sector: [], country: [], competitors: [], year: null, query: "" })
  }

  const fetchFilterOptions = async () => {
    setLoading(true)
    try {
      const url = `${process.env.REACT_APP_BASE_API_URL}csrd-benchmarking-dashboard-filters`
      const username = localStorage.getItem("userName")
      const res = await fetch(url, {
        method: "GET",
        headers: { "Content-type": "application/json; charset=UTF-8", username },
      })
      if (res.status === 200) {
        const responseJson = await res.json()
        setCompanyOptions(
          responseJson.companies_metadata.map((c) => ({ label: c.company_name, value: c.company_name })),
        )
        setSectorOptions(responseJson.sectors.map((s) => ({ label: s.label, value: s.label })))
        setCountryOptions(responseJson.countries.map((c) => ({ label: c.label, value: c.label })))
        setCompetitorsOptions(
          responseJson.companies_metadata.map((c) => ({ label: c.company_name, value: c.company_name })),
        )
        setYearOptions(responseJson.years.map((y) => ({ label: y.label, value: y.label })))
      } else {
        console.error("Error fetching filter options, status: " + res.status)
      }
    } catch (error) {
      console.error("Error fetching filter options:", error)
    } finally {
      setLoading(false)
    }
  }

  const fetchDatas = async () => {
    setLoading(true)
    try {
      const requestBody = {}
      const username = localStorage.getItem("userName")
      const companyid = localStorage.getItem("companyid")

      const hasCompanyOrCompetitors = !!(
        formData.companyName ||
        (formData.competitors && formData.competitors.length > 0)
      )

      if (formData.year) {
        requestBody.year = formData.year
      } else if (hasCompanyOrCompetitors) {
        requestBody.year = 2024 // Default year
      }
      if (formData.companyName) {
        requestBody.primary_company = formData.companyName
      }
      if (formData.competitors && formData.competitors.length > 0) {
        requestBody.competitors = formData.competitors
      }

      const bodyToSend = Object.keys(requestBody).length === 0 ? "" : JSON.stringify(requestBody)
      const apiUrl = `${process.env.REACT_APP_BASE_API_URL}csrd-benchmarking-dashboard`

      const response = await fetch(apiUrl, {
        method: "POST",
        headers: { "Content-Type": "application/json", username, companyid },
        body: bodyToSend,
      })

      if (response.status === 200) {
        const responseJson = await response.json()
        setBenchmarkData(responseJson)
        if (responseJson && responseJson.charts) {
          const iroVisualData = responseJson.charts.iro_visual_format
            ? Object.entries(responseJson.charts.iro_visual_format).map(([name, value]) => ({
                name,
                value,
                color: "#CCCCCC",
              }))
            : []
          setChartData({
            external_assurance_percent: responseJson.charts.external_assurance_percent || 0,
            eu_taxonomy_percent: responseJson.charts.eu_taxonomy_percent || 0,
            companies_by_country: responseJson.charts.companies_by_country || [],
            emissions_profile: responseJson.charts.emissions_profile || [],
            workforce_and_board_diversity: responseJson.charts.workforce_and_board_diversity || [],
            iro_visual_format: iroVisualData,
            esrs_standards_mentioned: responseJson.tables?.esrs_standards_mentioned || [],
            framework_alignment: responseJson.tables?.framework_alignment || [],
          })
        } else {
          setChartData({
            external_assurance_percent: 0,
            eu_taxonomy_percent: 0,
            companies_by_country: [],
            emissions_profile: [],
            workforce_and_board_diversity: [],
            iro_visual_format: {},
            esrs_standards_mentioned: [],
            framework_alignment: [],
          })
        }
        if (responseJson && responseJson.kpis) {
          setKpiData({
            company_count: responseJson.kpis.company_count || 0,
            average_csrd_page_count: responseJson.kpis.average_csrd_page_count || 0,
            average_material_topics_count: responseJson.kpis.average_material_topics_count || 0,
          })
        } else {
          setKpiData({ company_count: 0, average_csrd_page_count: 0, average_material_topics_count: 0 })
        }
      } else {
        console.error(`API Error: ${apiUrl}, Status: ${response.status}`)
        setBenchmarkData({})
        setChartData({
          external_assurance_percent: 0,
          eu_taxonomy_percent: 0,
          companies_by_country: [],
          emissions_profile: [],
          workforce_and_board_diversity: [],
          iro_visual_format: {},
          esrs_standards_mentioned: [],
          framework_alignment: [],
        })
        setKpiData({ company_count: 0, average_csrd_page_count: 0, average_material_topics_count: 0 })
      }
    } catch (error) {
      console.error("Error fetching data:", error)
    } finally {
      setLoading(false)
    }
  }

  const fetchSectionsData = async () => {
    setLoading(true)
    try {
      const requestBody = {}
      const username = localStorage.getItem("userName")
      const companyid = localStorage.getItem("companyid")

      const hasCompanyOrCompetitors = !!(
        formData.companyName ||
        (formData.competitors && formData.competitors.length > 0)
      )

      if (formData.year) {
        requestBody.year = formData.year
      } else {
        requestBody.year = 0 // Default year
      }
      if (formData.companyName) {
        requestBody.primary_company = formData.companyName
      } else {
        requestBody.primary_company = ""
      }

      if (formData.competitors && formData.competitors.length > 0) {
        requestBody.competitors = formData.competitors
      } else {
        requestBody.competitors = []
      }
      const bodyToSend = Object.keys(requestBody).length === 0 ? "" : JSON.stringify(requestBody)
      const apiUrl = `${process.env.REACT_APP_BASE_API_URL}deep-dive-sections`

      const response = await fetch(apiUrl, {
        method: "POST",
        headers: { "Content-Type": "application/json", username, companyid },
        body: bodyToSend,
      })

      if (response.status === 200) {
        const responseJson = await response.json()
        setSectionsData(responseJson)
      } else {
        console.error(`API Error: ${apiUrl}, Status: ${response.status}`)
        setSectionsData({})
      }
    } catch (error) {
      console.error("Error fetching data:", error)
    } finally {
      setLoading(false)
    }
  }

  const fetchQuotesComparisonData = async () => {
    setLoading(true)
    try {
      const requestBody = {}
      const username = localStorage.getItem("userName")
      const companyid = localStorage.getItem("companyid")
      if (formData.year) {
        requestBody.year = formData.year
      } else {
        requestBody.year = 0 // Default year
      }
      if (formData.companyName) {
        requestBody.primary_company = formData.companyName
      } else {
        requestBody.primary_company = ""
      }

      if (formData.competitors && formData.competitors.length > 0) {
        requestBody.competitors = formData.competitors
      } else {
        requestBody.competitors = []
      }
      const bodyToSend = Object.keys(requestBody).length === 0 ? "" : JSON.stringify(requestBody)
      const apiUrl = `${process.env.REACT_APP_BASE_API_URL}deep-dive-quotes-comparison`

      const response = await fetch(apiUrl, {
        method: "POST",
        headers: { "Content-Type": "application/json", username, companyid },
        body: bodyToSend,
      })

      if (response.status === 200) {
        const responseJson = await response.json()
        setQuotesComparisonData(responseJson)
      } else {
        console.error(`API Error: ${apiUrl}, Status: ${response.status}`)
        setQuotesComparisonData({})
      }
    } catch (error) {
      console.error("Error fetching data:", error)
    } finally {
      setLoading(false)
    }
  }

  const fetchChatData = async (query) => {
    setLoading(true)
    try {
      const requestBody = {}
      const username = localStorage.getItem("userName")
      const companyid = localStorage.getItem("companyid")
      if (formData1.year) {
        requestBody.year = formData1.year
      } else {
        requestBody.year = 0 // Default year
      }
      if (formData1.companyName) {
        requestBody.primary_company = formData1.companyName
      } else {
        requestBody.primary_company = ""
      }

      if (query) {
        requestBody.query = query
      } else {
        requestBody.query = ""
        setLoading(false)
        return false
      }
      const bodyToSend = Object.keys(requestBody).length === 0 ? "" : JSON.stringify(requestBody)
      const apiUrl = `${process.env.REACT_APP_BASE_API_URL}deep-dive-chat`

      const response = await fetch(apiUrl, {
        method: "POST",
        headers: { "Content-Type": "application/json", username, companyid },
        body: bodyToSend,
      })

      if (response.status === 200) {
        const responseJson = await response.json()
        setChatData(responseJson)
      } else {
        console.error(`API Error: ${apiUrl}, Status: ${response.status}`)
        setChatData({})
      }
    } catch (error) {
      console.error("Error fetching data:", error)
    } finally {
      setLoading(false)
    }
  }

  const fetchMaterialityComparisonData = async () => {
    setLoading(true)
    try {
      const requestBody = {}
      const username = localStorage.getItem("userName")
      const companyid = localStorage.getItem("companyid")
      if (formData.year) {
        requestBody.year = formData.year
      } else {
        requestBody.year = 0 // Default year
      }
      if (formData.companyName) {
        requestBody.primary_company = formData.companyName
      } else {
        requestBody.primary_company = ""
      }
      if (formData.competitors && formData.competitors.length > 0) {
        requestBody.competitors = formData.competitors
      } else {
        requestBody.competitors = []
      }
      const bodyToSend = Object.keys(requestBody).length === 0 ? "" : JSON.stringify(requestBody)
      const apiUrl = `${process.env.REACT_APP_BASE_API_URL}deep-dive-materiality-comparison`
      const response = await fetch(apiUrl, {
        method: "POST",
        headers: { "Content-Type": "application/json", username, companyid },
        body: bodyToSend,
      })
      if (response.status === 200) {
        const responseJson = await response.json()
        setMaterialityComparisonData(responseJson)
      } else {
        setMaterialityComparisonData({})
      }
    } catch (error) {
      setMaterialityComparisonData({})
    } finally {
      setLoading(false)
    }
  }

  const handleSubmitQuery = () => {
    if (!localQuery.trim()) return
    setFormData((prev) => ({ ...prev, query: localQuery }))
    fetchChatData(localQuery)
  }

  useEffect(() => {
    fetchFilterOptions()
  }, [])

  useEffect(() => {
    fetchDatas()
    fetchSectionsData()
    fetchQuotesComparisonData()
    fetchMaterialityComparisonData()
  }, [formData])

  const handleChange = (event, newValue) => {
    setActiveTab(newValue)
  }

  return (
    <Box sx={{ flexGrow: 1 }}>
      {/* Header */}
      <AppBar position="static" sx={{ backgroundColor: "#407775", boxShadow: "none" }}>
        <Toolbar sx={{ px: { xs: 2, md: 4 }, py: 1 }}>
          {/* Left side - Logo and Organization */}
          <Box sx={{ display: "flex", alignItems: "center", flexGrow: { xs: 1, lg: 0 } }}>
            <Box
              component="a"
              href="#home"
              sx={{
                display: "flex",
                alignItems: "center",
                textDecoration: "none",
                p: 0,
              }}
            >
              <Box component="img" src="../assets/img/logo.png" alt="Company Logo" sx={{ height: 30 }} />
            </Box>

            <Box
              sx={{
                mx: 2,
                borderLeft: "1px solid rgba(255, 255, 255, 0.5)",
                height: "30px",
                pl: 2.5,
                display: { xs: "none", sm: "block" },
              }}
            />

            <Typography
              variant="body2"
              sx={{
                color: "rgba(255, 255, 255, 0.7)",
                fontSize: "14px",
                display: { xs: "none", sm: "block" },
              }}
            >
              Organization Name
            </Typography>
          </Box>

          {/* Mobile menu button */}
          <IconButton
            color="inherit"
            aria-label="open drawer"
            edge="end"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            sx={{ display: { lg: "none" } }}
          >
            <MenuIcon />
          </IconButton>

          {/* Right side - Navigation (Desktop) */}
          <Box
            sx={{
              display: { xs: "none", lg: "flex" },
              alignItems: "center",
              gap: 2,
              ml: "auto",
            }}
          >
            <IconButton component="a" href="#notifications" color="inherit" sx={{ p: 1 }}>
              <NotificationsActive sx={{ width: "1.5rem", height: "1.5rem" }} />
            </IconButton>

            <IconButton component="a" href="#profile" color="inherit" sx={{ p: 1 }}>
              <Box
                component="img"
                src="../assets/img/User.png"
                alt="User"
                sx={{ width: 24, height: 24, borderRadius: "50%" }}
              />
            </IconButton>

            <Typography
              variant="body2"
              sx={{
                color: "rgba(255, 255, 255, 0.7)",
                fontSize: "14px",
                ml: 1,
              }}
            >
              XYZ ltd
            </Typography>
          </Box>

          {/* Mobile Navigation Menu */}
          {mobileMenuOpen && (
            <Box
              sx={{
                position: "absolute",
                top: "100%",
                left: 0,
                right: 0,
                backgroundColor: "#407775",
                display: { lg: "none" },
                flexDirection: "column",
                p: 2,
                gap: 2,
                borderTop: "1px solid rgba(255, 255, 255, 0.1)",
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                <IconButton component="a" href="#notifications" color="inherit" sx={{ p: 1 }}>
                  <NotificationsActive sx={{ width: "1.5rem", height: "1.5rem" }} />
                </IconButton>

                <IconButton component="a" href="#profile" color="inherit" sx={{ p: 1 }}>
                  <Box
                    component="img"
                    src="../assets/img/User.png"
                    alt="User"
                    sx={{ width: 24, height: 24, borderRadius: "50%" }}
                  />
                </IconButton>

                <Typography
                  variant="body2"
                  sx={{
                    color: "rgba(255, 255, 255, 0.7)",
                    fontSize: "14px",
                  }}
                >
                  XYZ ltd
                </Typography>
              </Box>
            </Box>
          )}
        </Toolbar>
      </AppBar>

      {/* Main Content */}
      <Container maxWidth="xl" sx={{ mt: 4 }}>
        <Typography variant="h4" gutterBottom>
          Deep Dive Analysis
        </Typography>

        <Box sx={{ mb: 3, p: 2, border: "1px solid #ddd", borderRadius: "8px", background: "#f9f9f9" }}>
          {loading && (
            <Backdrop sx={{ color: "#fff", zIndex: (theme) => theme.zIndex.drawer + 100 }} open={loading}>
              <CircularProgress color="inherit" />
            </Backdrop>
          )}
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} sm={6} md={2}>
              <Typography variant="caption" display="block" gutterBottom>
                Company Name
              </Typography>
              <Select
                name="companyName"
                value={companyOptions.find((o) => o.value === formData.companyName) || null}
                options={companyOptions}
                onChange={(sel) => setFormData((f) => ({ ...f, companyName: sel ? sel.value : null }))}
                placeholder="Search"
                isSearchable
                isClearable
                styles={customStyles}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <Typography variant="caption" display="block" gutterBottom>
                Sector
              </Typography>
              <Select
                name="sector"
                value={sectorOptions.filter((o) => formData.sector.includes(o.value))}
                options={sectorOptions}
                onChange={(sel) => setFormData((f) => ({ ...f, sector: sel ? sel.map((s) => s.value) : [] }))}
                placeholder="Search"
                isSearchable
                isClearable
                isMulti
                styles={customStyles}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <Typography variant="caption" display="block" gutterBottom>
                Country
              </Typography>
              <Select
                name="country"
                value={countryOptions.filter((o) => formData.country.includes(o.value))}
                options={countryOptions}
                onChange={(sel) => setFormData((f) => ({ ...f, country: sel ? sel.map((s) => s.value) : [] }))}
                placeholder="Search"
                isSearchable
                isClearable
                isMulti
                styles={customStyles}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Typography variant="caption" display="block" gutterBottom>
                Competitors (Up to 5)
              </Typography>
              <Select
                name="competitors"
                value={competitorsOptions.filter((o) => formData.competitors.includes(o.value))}
                options={competitorsOptions}
                onChange={(sel) => {
                  if (sel && sel.length <= 5) {
                    setFormData((f) => ({ ...f, competitors: sel.map((s) => s.value) }))
                  } else if (!sel) {
                    setFormData((f) => ({ ...f, competitors: [] }))
                  }
                }}
                placeholder="Search"
                isSearchable
                isClearable
                isMulti
                styles={customStyles}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <Typography variant="caption" display="block" gutterBottom>
                Year
              </Typography>
              <Select
                name="year"
                value={yearOptions.find((o) => o.value === formData.year) || null}
                options={yearOptions}
                onChange={(sel) => setFormData((f) => ({ ...f, year: sel ? sel.value : null }))}
                placeholder="Search"
                isSearchable
                isClearable
                styles={customStyles}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={1} sx={{ display: "flex", alignItems: "flex-end", height: "100%" }}>
              <Button
                onClick={resetSelections}
                variant="outlined"
                style={{
                  width: "100%",
                  minWidth: "auto",
                  height: "38px",
                  color: "#407775",
                  borderColor: "#407775",
                  textTransform: "none",
                  padding: "6px 10px",
                }}
                title="Reset Filters"
              >
                <MdRefresh style={{ fontSize: "20px" }} />
              </Button>
            </Grid>
          </Grid>
        </Box>

        <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
          <Tabs
            value={activeTab}
            onChange={handleChange}
            aria-label="Deep Dive Tabs"
            variant="scrollable"
            scrollButtons="auto"
            sx={{
              "& .MuiTabs-indicator": {
                backgroundColor: "#407775",
                height: 3,
              },
              "& .MuiTab-root": {
                color: "#707070",
                fontWeight: 500,
                textTransform: "none",
                fontSize: "14px",
                minHeight: 48,
                "&:hover": {
                  color: "#407775",
                  backgroundColor: "rgba(64, 119, 117, 0.04)",
                },
                "&.Mui-selected": {
                  color: "#407775",
                  fontWeight: 600,
                },
              },
              "& .MuiTabs-scrollButtons": {
                color: "#407775",
                "&.Mui-disabled": {
                  opacity: 0.3,
                },
              },
            }}
          >
            {TABS.map((tab, index) => (
              <Tab
                label={tab.label}
                key={tab.label}
                id={`deep-dive-tab-${index}`}
                aria-controls={`deep-dive-tabpanel-${index}`}
                sx={{
                  ...(tab.comingSoon && {
                    color: "#999 !important",
                    fontStyle: "italic",
                    "&:hover": {
                      color: "#666 !important",
                      backgroundColor: "rgba(153, 153, 153, 0.04)",
                    },
                  }),
                }}
              />
            ))}
          </Tabs>
        </Box>
        {TABS.map((tab, index) => {
          const SpecificComponent = tab.component
          return (
            <TabPanel value={activeTab} index={index} key={tab.label}>
              {tab.comingSoon ? (
                <ComingSoon />
              ) : tab.label === "Materiality" ? (
                <MaterialityComparison data={materialityComparisonData} />
              ) : tab.label === "Quotes" ? (
                <QuotesComparison quotesComparisonData={quotesComparisonData} />
              ) : tab.label === "Chat" ? (
                <Chat
                  formData={formData1}
                  setFormData={setFormData1}
                  chatData={chatData}
                  fetchChatData={fetchChatData}
                  loading={loading}
                />
              ) : (
                <SpecificComponent
                  formData={formData}
                  benchmarkData={benchmarkData}
                  kpiData={kpiData}
                  chartData={chartData}
                  loading={loading}
                  fetchDatas={fetchDatas}
                  sectionsData={fetchSectionsData}
                  quotesComparisonData={fetchQuotesComparisonData}
                  chatData={fetchChatData}
                />
              )}
            </TabPanel>
          )
        })}
      </Container>
    </Box>
  )
}

export default DeepDivePage
